// src/components/Staff/SupportRecordSection.tsx
import { useState } from 'react';
import { useEffect } from 'react';
import { Disclosure, DisclosurePanel, Heading, Button as AriaButton } from 'react-aria-components';
import { useForm } from 'react-hook-form';

import Button from '@/components/Button/Button';
import MultiFileUpload from '@/components/MultiFileUpload/MultiFileUpload';
import SelectField from '@/components/SelectField/SelectField';
import { SelectTemplateDialog } from '@/components/templates/SelectTemplateDialog';
import { SupportRecord } from '@/types/supportRecord';
import { LineAccountUser } from '@/types/user';

type Props = {
  record: SupportRecord;
  displayDate?: string;
  editedValues: Partial<SupportRecord>;
  onFieldChange: (
    field: keyof SupportRecord | 'imageFiles',
    value: string | number | File[]
  ) => void;
  conditionTemplates: { id: number; body: string; category: string }[];
  lineUser: LineAccountUser;
};

const SupportRecordSection = ({
  record,
  editedValues,
  onFieldChange,
  conditionTemplates,
  lineUser,
  displayDate,
}: Props) => {
  console.log('[DEBUG] record:', record);
  console.log('[DEBUG] record.support_record_images:', record.support_record_images);

  const fullName = `${record.child?.last_name ?? ''} ${record.child?.first_name ?? ''}`;

  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [templateLoading, _setTemplateLoading] = useState(false);

  const [templateTargetField, setTemplateTargetField] = useState<keyof SupportRecord | null>(null);
  const [templateCategory, setTemplateCategory] = useState<string>('');
  const handleOpenTemplateDialog = (field: keyof SupportRecord, category: string) => {
    if (!conditionTemplates.length) {
      alert('テンプレートが見つかりませんでした');
      return;
    }
    setTemplateTargetField(field);
    setTemplateCategory(category);
    setIsTemplateModalOpen(true);
  };

  const attendanceStatus = editedValues.attendance_status || record.attendance_status;

  const ATTENDANCE_OPTIONS = [
    { label: '出席', value: 'attend' },
    { label: '欠席', value: 'absent' },
  ];
  const { control, watch } = useForm<{ attendance_status: string }>({
    defaultValues: {
      attendance_status: editedValues.attendance_status ?? record.attendance_status ?? 'attend',
    },
  });

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.attendance_status) {
        onFieldChange('attendance_status', value.attendance_status);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <div className="space-y-4">
      <div className="rounded-8 bg-blue-100 px-2 py-3">
        <Heading className="text-lg font-bold">{displayDate}</Heading>
        <Heading className="text-lg font-bold">{fullName}</Heading>
      </div>
      <div className="text-right font-bold">記録担当者: {lineUser.staff_name ?? '未取得'}</div>

      {/* 利用状況 */}
      <Disclosure className="h-full rounded-md bg-gray-100">
        <Heading>
          <AriaButton
            className="flex w-full flex-row items-center justify-between p-4 py-4"
            slot="trigger"
          >
            <div className="text-lg font-bold">利用状況</div>
          </AriaButton>
        </Heading>
        <DisclosurePanel>
          <div className="space-y-6 p-4">
            <div className="text-base font-bold leading-relaxed">提供時間開始</div>
            <div className="text-base leading-relaxed">{record.start_time ?? '未入力'}</div>
            <hr className="border-gray-400" />

            <div className="text-base font-bold leading-relaxed">提供時間終了</div>
            <div className="text-base leading-relaxed">{record.end_time ?? '未入力'}</div>
            <hr className="border-gray-400" />

            <div className="text-base font-bold leading-relaxed">出欠情報</div>
            <SelectField
              name="attendance_status"
              label="出欠情報"
              control={control}
              items={ATTENDANCE_OPTIONS}
              required
            />
            <hr className="border-gray-400" />

            {attendanceStatus === 'absent' && (
              <>
                <div className="text-base font-bold leading-relaxed">欠席連絡日</div>
                <div className="text-base leading-relaxed">{record.absent_date || '未入力'}</div>
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">
                  欠席理由・申し送り事項（保護者）
                </div>
                <div className="text-base leading-relaxed">
                  {record.absent_reason_by_guardian || '未入力'}
                </div>
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">
                  欠席理由・申し送り事項（事業所）
                </div>
                <textarea
                  value={editedValues.absent_reason_by_staff ?? record.absent_reason_by_staff ?? ''}
                  onChange={(e) => onFieldChange('absent_reason_by_staff', e.target.value)}
                  className="w-full rounded border px-2 py-1"
                  placeholder="欠席理由（事業所）を入力"
                />
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">振替</div>
                <div className="text-base leading-relaxed">
                  {record.rescheduled_date || '未入力'}
                </div>
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">欠席時加算</div>
                <div className="text-base leading-relaxed">
                  {record.is_absent_addition ? 'あり' : 'なし'}
                </div>
                <hr className="border-gray-400" />
              </>
            )}

            {attendanceStatus === 'attend' && (
              <>
                <div className="text-base font-bold leading-relaxed">送迎（来所）</div>
                <input
                  type="time"
                  value={editedValues.pickup_or_arrival_time ?? record.pickup_or_arrival_time ?? ''}
                  onChange={(e) => onFieldChange('pickup_or_arrival_time', e.target.value)}
                  className="w-full rounded border px-2 py-1"
                  placeholder="送迎（来所）を入力"
                />
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">送迎加算（来所）</div>
                <div className="text-base leading-relaxed">
                  {record.is_pickup_addition ? 'あり' : 'なし'}
                </div>
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">送迎（退所）</div>
                <input
                  type="time"
                  value={
                    editedValues.dropoff_or_departure_time ?? record.dropoff_or_departure_time ?? ''
                  }
                  onChange={(e) => onFieldChange('dropoff_or_departure_time', e.target.value)}
                  className="w-full rounded border px-2 py-1"
                  placeholder="送迎（退所）を入力"
                />
                <hr className="border-gray-400" />

                <div className="text-base font-bold leading-relaxed">送迎加算（退所）</div>
                <div className="text-base leading-relaxed">
                  {record.is_dropoff_addition ? 'あり' : 'なし'}
                </div>
                <hr className="border-gray-400" />
              </>
            )}
          </div>
        </DisclosurePanel>
      </Disclosure>

      {/* バイタル */}
      <Disclosure className="h-full rounded-md bg-gray-100">
        <Heading>
          <AriaButton
            className="flex w-full flex-row items-center justify-between p-4 py-4"
            slot="trigger"
          >
            <div className="text-lg font-bold">バイタル</div>
          </AriaButton>
        </Heading>
        <DisclosurePanel>
          <div className="space-y-6 p-4">
            <div className="text-base font-bold leading-relaxed">体温</div>
            <input
              type="number"
              value={editedValues.body_temperature ?? record.body_temperature ?? ''}
              onChange={(e) => onFieldChange('body_temperature', parseFloat(e.target.value))}
              className="w-full rounded border px-2 py-1"
              placeholder="体温を入力"
            />
            <hr className="border-gray-400" />

            <div className="text-base font-bold leading-relaxed">体調メモ</div>
            {/* 体調メモボタン */}
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleOpenTemplateDialog('condition', '体調メモ')}
              disabled={templateLoading}
            >
              {templateLoading ? '読み込み中...' : '定型文から入力'}
            </Button>

            <textarea
              value={editedValues.condition ?? record.condition ?? ''}
              onChange={(e) => onFieldChange('condition', e.target.value)}
              className="w-full rounded border px-2 py-1"
              placeholder="体調について記入"
            />
            <hr className="border-gray-400" />

            {isTemplateModalOpen && templateTargetField && (
              <SelectTemplateDialog
                isOpen={isTemplateModalOpen}
                setIsOpen={setIsTemplateModalOpen}
                templates={conditionTemplates.filter(
                  (template) => template.category === templateCategory
                )}
                onSubmit={(selectedTemplate) => {
                  onFieldChange(templateTargetField, selectedTemplate.body);
                  setIsTemplateModalOpen(false);
                }}
                label={templateCategory}
              />
            )}
          </div>
        </DisclosurePanel>
      </Disclosure>

      {/* 支援・療育記録 */}
      <Disclosure className="h-full rounded-md bg-gray-100">
        <Heading>
          <AriaButton
            className="flex w-full flex-row items-center justify-between p-4 py-4"
            slot="trigger"
          >
            <div className="text-lg font-bold">支援・療育記録</div>
          </AriaButton>
        </Heading>
        <DisclosurePanel>
          <div className="space-y-6 p-4">
            <div className="text-base font-bold leading-relaxed">支援内容</div>
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleOpenTemplateDialog('support_content', '支援記録')}
              disabled={templateLoading}
            >
              {templateLoading ? '読み込み中...' : '定型文から入力'}
            </Button>

            <textarea
              value={editedValues.support_content ?? record.support_content ?? ''}
              onChange={(e) => onFieldChange('support_content', e.target.value)}
              className="w-full rounded border px-2 py-1"
              placeholder="支援内容を記入"
            />
            <hr className="border-gray-400" />

            <div className="text-base font-bold leading-relaxed">連絡事項（保護者向け）</div>
            <Button
              size="xs"
              variant="outline"
              onClick={() => handleOpenTemplateDialog('staff_message', '連絡帳')}
              disabled={templateLoading}
            >
              {templateLoading ? '読み込み中...' : '定型文から入力'}
            </Button>
            <textarea
              value={editedValues.staff_message ?? record.staff_message ?? ''}
              onChange={(e) => onFieldChange('staff_message', e.target.value)}
              className="w-full rounded border px-2 py-1"
              placeholder="保護者向けの連絡事項"
            />
            <hr className="border-gray-400" />

            <div className="text-base font-bold leading-relaxed">業務日誌（スタッフ向け）</div>
            <textarea
              value={editedValues.staff_report ?? record.staff_report ?? ''}
              onChange={(e) => onFieldChange('staff_report', e.target.value)}
              className="w-full rounded border px-2 py-1"
              placeholder="スタッフ向けの共有事項"
            />
            {/* 写真 */}
            <hr className="border-gray-400" />
            <div className="w-full">
              <MultiFileUpload
                label="写真アップロード"
                buttonText="画像アップロード"
                canPreview
                accept="image/*"
                setFiles={(files) => {
                  onFieldChange('imageFiles', files);
                }}
                initialValues={record.support_record_images ?? []}
              />

              <hr className="border-gray-400" />
            </div>
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
};

export default SupportRecordSection;
